import React, { useState, useEffect } from 'react';
import styled from '@emotion/native';
import {
  UserMessage,
  BaseMessage,
  type FileMessage,
} from '@sendbird/chat/message';
import type { GroupChannel } from '@sendbird/chat/groupChannel';
import { Image, TouchableOpacity } from 'react-native';
import Button from '@/shared/components/Button';
import {
  white,
  lightGreyAlt,
  lightGrey,
  alphaBlack05,
  lightGreen,
} from '@/shared/styles/colors';
import { useCreateLookbook } from '../useCreateLookbook';
import LookbookPurchaseModal from '../LookbookPurchaseModal';
import { BaseText, H4 } from '@/shared/components/StyledText';
import SentryService from '@/services/sentry/Sentry.service';
import useStore from '@/stores/useStore';
import { Lookbook, LookbookStatus } from '@/shared/types/generated';
import { router } from 'expo-router';
import { ROUTES } from '@/shared/constants/routes';
import { useMutation, useQuery } from '@apollo/client';
import { CREATE_PAYMENT_INTENT } from '@/shared/mutations';
import {
  MutationResult,
  MutationCreatePaymentIntentArgs,
  QueryResult,
  QueryLookbookArgs,
} from '@/shared/types/generated';
import { UPDATE_LOOKBOOK_STATUS } from '@/shared/mutations';
import {
  MutationResult as MutationResultUpdateLookbookStatus,
  MutationUpdateLookbookStatusArgs,
} from '@/shared/types/generated';
import { formatNameWithLastInitial } from '@/shared/utils';
import { GET_LOOKBOOK } from '@/shared/queries';

/** Matches Sendbird UIKit deleteMessage; product messages are UserMessage. */
type DeleteMessageFn = (message: UserMessage | FileMessage) => Promise<void>;

interface ProductMessageProps {
  message: BaseMessage;
  channel?: GroupChannel;
  currentUserId?: string;
  onDeleteMessage?: DeleteMessageFn;
}

interface CustomMessageData {
  price: string;
  stylistId?: number;
  note?: string;
}

const ProductMessage: React.FC<ProductMessageProps> = ({
  message,
  channel,
  currentUserId,
  onDeleteMessage,
}) => {
  const isStylist = useStore((state) => state.isStylist);
  const setSnackbarState = useStore((state) => state.setSnackbarState);
  const lookbookPurchasedMessageId = useStore(
    (state) => state.lookbookPurchasedMessageId,
  );
  const setLookbookPurchasedMessageId = useStore(
    (state) => state.setLookbookPurchasedMessageId,
  );
  const { createLookbook, loading: isCreatingLookbook } = useCreateLookbook();
  const [isPurchaseModalVisible, setIsPurchaseModalVisible] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [lookbookData, setLookbookData] = useState<Lookbook | null>(null);

  const [createPaymentIntent] = useMutation<
    MutationResult<'createPaymentIntent'>,
    MutationCreatePaymentIntentArgs
  >(CREATE_PAYMENT_INTENT);
  const [updateLookbookStatus] = useMutation<
    MutationResultUpdateLookbookStatus<'updateLookbookStatus'>,
    MutationUpdateLookbookStatusArgs
  >(UPDATE_LOOKBOOK_STATUS);

  // Query for existing lookbook by sendbirdMessageId
  const {
    data: lookbookQueryData,
    loading: isLoadingLookbook,
    refetch,
  } = useQuery<QueryResult<'lookbook'>, QueryLookbookArgs>(GET_LOOKBOOK, {
    variables: {
      input: {
        sendbirdMessageId: message.messageId.toString(),
      },
    },
    onError: (error) => {
      // Don't show error if lookbook doesn't exist yet
      if (
        !error.graphQLErrors?.some((e) => e.extensions?.code === 'NOT_FOUND')
      ) {
        SentryService.captureError('Error fetching lookbook:', error);
      }
    },
  });

  const existingLookbook = lookbookQueryData?.lookbook;

  // Watch for lookbook purchase events and refetch if this message matches
  useEffect(() => {
    if (
      lookbookPurchasedMessageId &&
      lookbookPurchasedMessageId === message.messageId.toString() &&
      isStylist
    ) {
      refetch();
      // Clear the message ID after handling
      setLookbookPurchasedMessageId(null);
    }
  }, [
    lookbookPurchasedMessageId,
    message.messageId,
    isStylist,
    refetch,
    setLookbookPurchasedMessageId,
  ]);

  // Type guard to ensure we have a UserMessage
  if (!(message instanceof UserMessage)) {
    SentryService.captureError(
      'ProductMessage: Expected UserMessage but received different message type',
    );
    return null;
  }

  try {
    const customData = message.data;
    const data: CustomMessageData =
      typeof customData === 'string' ? JSON.parse(customData) : customData;

    if (!data?.price) {
      SentryService.captureError(
        'ProductMessage: Price information is missing',
      );
      return null;
    }

    if (!data?.stylistId) {
      SentryService.captureError('ProductMessage: Stylist ID is missing');
      return null;
    }

    const getStylistName = () => {
      const lookbook = existingLookbook || lookbookData;
      if (lookbook?.stylist?.user) {
        return formatNameWithLastInitial(lookbook.stylist.user);
      }
      return 'Unknown Stylist';
    };

    const onPressPurchase = async () => {
      if (isStylist) {
        // Only clients can purchase lookbooks
        return;
      }

      try {
        let lookbook: Lookbook;

        if (existingLookbook) {
          // Use existing lookbook if it exists
          lookbook = existingLookbook;
        } else {
          // Create new lookbook if it doesn't exist
          const priceInCents = Math.round(parseFloat(data.price) * 100);
          lookbook = await createLookbook({
            sendbirdMessageId: message.messageId.toString(),
            price: priceInCents,
            stylistId: data.stylistId!,
            ...(data.note?.trim() && { note: data.note.trim() }),
          });
        }

        setLookbookData(lookbook);
        setIsPurchaseModalVisible(true);
      } catch (error) {
        SentryService.captureError('Error creating lookbook:', error);
      }
    };

    const handleCheckout = async () => {
      setIsProcessingPayment(true);
      try {
        if (!lookbookData) {
          throw new Error('Lookbook data missing');
        }

        const paymentResult = await createPaymentIntent({
          variables: {
            input: {
              stylistId: lookbookData.stylistId,
              amount: lookbookData.price,
              captureImmediately: true,
            },
          },
        });

        const paymentId = paymentResult.data?.createPaymentIntent?.id;

        if (!paymentId) {
          throw new Error('Payment intent creation failed');
        }

        // Update lookbook status to COMPLETED and link payment
        await updateLookbookStatus({
          variables: {
            input: {
              sendbirdMessageId: lookbookData.sendbirdMessageId,
              status: LookbookStatus.Completed,
              paymentId: paymentId,
            },
          },
        });

        refetch();

        setIsPurchaseModalVisible(false);
        setSnackbarState({
          open: true,
          message: 'Lookbook purchased successfully!',
          severity: 'success',
        });
      } catch (error) {
        SentryService.captureError('Error processing payment:', error);
        setSnackbarState({
          open: true,
          message: 'Payment failed. Please try again.',
          severity: 'error',
        });
        // Keep modal open on error
      } finally {
        setIsProcessingPayment(false);
      }
    };

    const handleHelpCenterPress = () => {
      setIsPurchaseModalVisible(false);
      router.push(ROUTES.HELP.path);
    };

    const handleDelete = async () => {
      if (isDeleting) {
        return;
      }
      setIsDeleting(true);
      try {
        if (onDeleteMessage) {
          await onDeleteMessage(message);
        } else if (channel) {
          await channel.deleteMessage(message);
        }
      } catch (error) {
        SentryService.captureError('Error deleting product message:', error);
        setSnackbarState({
          open: true,
          message: 'Failed to delete message. Please try again.',
          severity: 'error',
        });
      } finally {
        setIsDeleting(false);
      }
    };

    const canDelete =
      !!channel &&
      !!currentUserId &&
      'sender' in message &&
      message.sender?.userId === currentUserId;
    const isCompleted = existingLookbook?.status === LookbookStatus.Completed;
    const showDeleteButton =
      canDelete && (!!onDeleteMessage || !!channel) && !isCompleted;
    const descriptionText = isStylist
      ? "Additional paid time for your services to create lookbooks, source pieces, or plan your client's wardrobe"
      : 'Additional paid time to your stylist to create lookbooks, source pieces or plan your wardrobe';

    return (
      <>
        <MessageContainer>
          <ContentContainer>
            <Title>Backend Hours</Title>
            {data.note?.trim() ? (
              <NoteText>{data.note.trim()}</NoteText>
            ) : (
              <Description>{descriptionText}</Description>
            )}

            {isCompleted ? (
              <PurchasedContainer>
                <CheckmarkCircle>
                  <Image
                    source={require('@/assets/images/CheckMarkWhite.png')}
                    style={{ width: 10, height: 10 }}
                    resizeMode="contain"
                  />
                </CheckmarkCircle>
                <PurchasedText>Purchase Confirmed</PurchasedText>
              </PurchasedContainer>
            ) : (
              <Button
                text={`Purchase - $${data.price}`}
                onPress={onPressPurchase}
                customStyle={buttonStyles}
                customLabelStyle={buttonLabelStyles}
                disabled={isCreatingLookbook || isLoadingLookbook}
              />
            )}

            {showDeleteButton && (
              <DeleteButtonContainer>
                <TouchableOpacity
                  onPress={handleDelete}
                  disabled={isDeleting}
                  accessibilityRole="button"
                  accessibilityLabel="Delete message"
                  hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                >
                  <Image
                    source={require('@/assets/images/DeleteIcon.png')}
                    style={{ width: 20, height: 20 }}
                    resizeMode="contain"
                  />
                </TouchableOpacity>
              </DeleteButtonContainer>
            )}
          </ContentContainer>
        </MessageContainer>

        <LookbookPurchaseModal
          isVisible={isPurchaseModalVisible}
          setIsVisible={setIsPurchaseModalVisible}
          price={Math.round(parseFloat(data.price) * 100)} // Convert to cents
          stylistName={getStylistName()}
          onCheckout={handleCheckout}
          onHelpCenterPress={handleHelpCenterPress}
          isProcessing={isProcessingPayment}
        />
      </>
    );
  } catch (error) {
    SentryService.captureError('Error parsing product message:', error);
    return null;
  }
};

const MessageContainer = styled.View`
  border-radius: 8px;
  background-color: ${lightGreyAlt};
  justify-content: center;
  margin-vertical: 8px;
  width: calc(100% + 48px);
  margin-horizontal: -24px;
  padding-vertical: 24px;
`;

const ContentContainer = styled.View({
  alignItems: 'center',
  backgroundColor: white,
  alignSelf: 'center',
  paddingHorizontal: 30,
  paddingTop: 12,
  paddingBottom: 18,
  borderWidth: 1,
  borderColor: lightGrey,
  shadowOffset: { width: 0, height: 4 },
  shadowColor: alphaBlack05,
  shadowOpacity: 1,
  shadowRadius: 4,
  maxWidth: 297,
  position: 'relative' as const,
});

const DeleteButtonContainer = styled.View({
  position: 'absolute' as const,
  bottom: 8,
  right: 8,
});

const Title = styled(H4)({
  fontSize: 20,
  textAlign: 'center',
  textTransform: 'uppercase',
});

const Description = styled(BaseText)({
  textAlign: 'center',
  fontSize: 12,
  marginBottom: 12,
  color: lightGreen,
  lineHeight: 16.2,
  letterSpacing: 0.12,
});

const NoteText = styled(BaseText)({
  textAlign: 'center',
  fontSize: 11,
  marginBottom: 12,
  color: lightGreen,
  lineHeight: 15,
  fontStyle: 'italic',
  paddingHorizontal: 8,
});

const PurchasedContainer = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
  marginBottom: 10,
});

const CheckmarkCircle = styled.View({
  width: 16,
  height: 16,
  borderRadius: 10,
  backgroundColor: lightGreen,
  justifyContent: 'center',
  alignItems: 'center',
});

const PurchasedText = styled(BaseText)({
  fontSize: 9,
  fontWeight: 'bold',
  color: lightGreen,
  marginLeft: 8,
});

const buttonStyles = {
  height: 32,
  width: 114,
};

const buttonLabelStyles = {
  fontSize: 8,
  letterSpacing: 0.41,
};

export default ProductMessage;
